import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import {
    Check,
    Copy,
    Zap,
    Lightbulb,
    ExternalLink
} from 'lucide-react';
import { INTEGRATION_GUIDES, type IntegrationTab, type IntegrationStep } from '@/data/integration-docs';
import { Link } from 'react-router-dom';
import { InteractiveSnippet } from '@/components/common/InteractiveSnippet';

// Helper to render text with ChytBot as a backlink and **bold** text
const renderWithBacklinks = (text: string) => {
    // First handle bold text wrapped in **
    const boldPattern = /\*\*([^*]+)\*\*/g;
    const textWithBold = text.replace(boldPattern, '%%%BOLD_START%%%$1%%%BOLD_END%%%');

    // Split by ChytBot and bold markers
    const parts = textWithBold.split(/(ChytBot|%%%BOLD_START%%%|%%%BOLD_END%%%)/gi);

    let isBold = false;
    return parts.map((part, index) => {
        if (part === '%%%BOLD_START%%%') {
            isBold = true;
            return null;
        }
        if (part === '%%%BOLD_END%%%') {
            isBold = false;
            return null;
        }
        if (part.toLowerCase() === 'chytbot') {
            return (
                <a
                    key={index}
                    href="https://www.chytbot.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn("text-primary hover:text-primary/90 transition-colors", isBold && "font-semibold")}
                >
                    ChytBot
                </a>
            );
        }
        if (isBold) {
            return <strong key={index} className="font-semibold text-foreground">{part}</strong>;
        }
        return <span key={index}>{part}</span>;
    }).filter(Boolean);
};

interface IntegrationGuideProps {
    platformId: string;
    snippet: string;
    copySnippet: () => void;
    copied: boolean;
}

export const IntegrationGuide = ({ platformId, snippet, copySnippet, copied, isDashboard = false }: IntegrationGuideProps & { isDashboard?: boolean }) => {
    const guides: Record<string, { title?: string; name?: string; why: string; tabs?: IntegrationTab[]; steps?: IntegrationStep[]; desc?: string; snippet?: string }> = {
        ...INTEGRATION_GUIDES,
        custom: {
            title: 'Developer Setup',
            why: 'Integration guides for modern web frameworks and custom implementations.',
            tabs: [
                {
                    id: 'html',
                    label: 'HTML',
                    content: {
                        desc: 'Standard JavaScript integration for any website.',
                        steps: [
                            { title: 'Add Script Tag', desc: 'Paste the snippet below inside your **<head>** tag or before the closing **</body>** tag.' },
                            { title: 'Replace Bot ID', desc: 'Update **YOUR_BOT_ID** with your actual bot ID from the ChytBot dashboard.' }
                        ],
                        snippet: snippet
                    }
                },
                {
                    id: 'react',
                    label: 'React',
                    content: {
                        desc: 'Load the chatbot script dynamically using useEffect.',
                        steps: [
                            { title: 'Add useEffect Hook', desc: 'Add the script loading logic inside a useEffect hook in your App.tsx or root layout.' },
                            { title: 'Set Your Bot ID', desc: 'Replace YOUR_BOT_ID with your actual bot ID.' }
                        ],
                        snippet: `useEffect(() => {
  const script = document.createElement('script');
  script.src = "https://www.chytbot.com/loader.js";
  script.setAttribute('data-bot-id', 'YOUR_BOT_ID');
  script.defer = true;
  document.body.appendChild(script);

  return () => {
    document.body.removeChild(script);
  };
}, []);`
                    }
                },
                {
                    id: 'next',
                    label: 'Next.js',
                    content: {
                        desc: 'Use the next/script component for optimal performance.',
                        steps: [
                            { title: 'Import Script', desc: "Import Script from 'next/script' in your layout or page." },
                            { title: 'Add Component', desc: 'Place the Script component in your JSX with your bot ID.' }
                        ],
                        snippet: `import Script from 'next/script'

<Script 
  src="https://www.chytbot.com/loader.js" 
  data-bot-id="YOUR_BOT_ID"
  strategy="lazyOnload" 
/>`
                    }
                },
                {
                    id: 'vue',
                    label: 'Vue.js',
                    content: {
                        desc: 'Load the script in the onMounted lifecycle hook.',
                        steps: [
                            { title: 'Add onMounted', desc: 'Use onMounted in your App.vue or layout component.' }
                        ],
                        snippet: `import { onMounted } from 'vue'

onMounted(() => {
  const script = document.createElement('script')
  script.src = "https://www.chytbot.com/loader.js"
  script.setAttribute('data-bot-id', 'YOUR_BOT_ID')
  script.async = true
  document.body.appendChild(script)
})`
                    }
                }
            ]
        }
    };

    const guide = guides[platformId];
    const [activeTab, setActiveTab] = useState(guide?.tabs?.[0]?.id || 'html');

    useEffect(() => {
        if (guide?.tabs) {
            setActiveTab(guide.tabs[0].id);
        }
    }, [platformId]);

    if (!guide) return null;

    const currentContent = guide?.tabs ? guide.tabs.find((t: IntegrationTab) => t.id === activeTab)?.content : guide;
    const currentSnippet = currentContent?.snippet || snippet;

    return (
        <div className="space-y-8 sm:space-y-10">
            {/* Header */}
            <div className="max-w-3xl">
                <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-2 sm:mb-3">
                    {guide.title || guide.name}
                </h2>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {guide.why}
                </p>
            </div>

            {/* Tabs */}
            {guide.tabs && (
                <div className="flex flex-wrap gap-1.5 sm:gap-2 p-1 bg-muted/30 rounded-lg sm:rounded-xl w-fit border border-border/50">
                    {guide.tabs.map((tab: IntegrationTab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={cn(
                                "px-3 sm:px-4 py-1.5 sm:py-2 rounded-md sm:rounded-lg text-xs sm:text-sm font-semibold transition-all",
                                activeTab === tab.id
                                    ? "bg-card text-primary shadow-sm ring-1 ring-border/50"
                                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                            )}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Tab Description */}
            {currentContent?.desc && (
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-3xl">
                    {renderWithBacklinks(currentContent.desc)}
                </p>
            )}

            {/* Steps */}
            <div className="space-y-8 sm:space-y-12">
                {currentContent?.steps?.map((step: IntegrationStep, i: number) => (
                    <div key={i} className="space-y-4 sm:space-y-5 max-w-3xl">
                        <div className="space-y-1.5 sm:space-y-2">
                            <div className="flex items-center gap-2.5 sm:gap-3">
                                <span className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary text-primary-foreground font-bold text-[9px] sm:text-[10px] shrink-0">
                                    {i + 1}
                                </span>
                                <h4 className="font-semibold text-base sm:text-lg text-foreground">{step.title}</h4>
                            </div>
                            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed pl-[30px] sm:pl-9">
                                {renderWithBacklinks(step.desc)}
                            </p>
                        </div>

                        {step.image && (
                            <div className="rounded-lg sm:rounded-xl border border-border overflow-hidden bg-muted/20 ml-[30px] sm:ml-9">
                                <div className="p-1 bg-muted flex justify-center">
                                    <img
                                        src={step.image}
                                        alt={step.imgAlt || step.title}
                                        className="max-h-[280px] sm:max-h-[350px] w-auto object-contain"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </div>
                            </div>
                        )}

                        {step.title.toLowerCase().includes('head') && (
                            <div className="bg-primary/10 border-l-4 border-primary p-3 sm:p-4 rounded-r-lg sm:rounded-r-xl flex gap-2.5 sm:gap-3 items-start ml-[30px] sm:ml-9">
                                <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 text-primary shrink-0 mt-0.5" />
                                <p className="text-xs sm:text-sm text-primary">
                                    Ensure the code is placed above the closing &lt;/head&gt; tag for optimal loading.
                                </p>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Code Snippet */}
            <div className="space-y-3 sm:space-y-4 pt-2 sm:pt-4">
                <label className="text-[9px] sm:text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                    {guide.tabs ? `${guide.tabs.find((t: IntegrationTab) => t.id === activeTab)?.label} Snippet` : 'Embed Code'}
                </label>
                <div className="relative group max-w-3xl">
                    <pre className="bg-slate-950 text-slate-50 p-4 sm:p-6 rounded-lg sm:rounded-xl overflow-x-auto text-[11px] sm:text-[13px] font-mono border border-slate-800 leading-relaxed">
                        <InteractiveSnippet text={currentSnippet} isDashboard={isDashboard} className="font-mono" />
                    </pre>
                    <button
                        onClick={() => {
                            navigator.clipboard.writeText(currentSnippet);
                            copySnippet();
                        }}
                        className={cn(
                            "absolute top-3 right-3 sm:top-4 sm:right-4 h-8 sm:h-9 px-3 sm:px-4 rounded-lg bg-background border border-border text-[10px] sm:text-xs font-semibold transition-all hover:bg-muted inline-flex items-center gap-1.5 sm:gap-2",
                            copied && "text-emerald-500 border-emerald-500/50"
                        )}
                    >
                        {copied ? <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> : <Copy className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}
                        {copied ? 'Copied' : 'Copy'}
                    </button>
                </div>
            </div>

            {/* ChytBot Backlink & Help Section */}
            <div className="pt-6 sm:pt-8 border-t border-border/50 space-y-4 sm:space-y-6">
                {/* External ChytBot Backlink */}
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    <span>Powered by</span>
                    <a
                        href="https://www.chytbot.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:text-primary/90 font-semibold transition-colors"
                    >
                        ChytBot AI
                        <ExternalLink className="w-3 h-3" />
                    </a>
                    <span>– The AI chatbot for modern businesses.</span>
                </div>

                {/* AI Assistance CTA */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-muted/30 p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-dashed border-border">
                    <div>
                        <h4 className="text-base sm:text-lg font-bold mb-1">Need AI Assistance?</h4>
                        <p className="text-sm text-muted-foreground">Use our pre-made prompts to let your AI coding assistant handle this for you.</p>
                    </div>
                    <Link
                        to="/ai-implementation"
                        className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-lg font-semibold text-sm hover:bg-primary/90 transition-colors shrink-0"
                    >
                        <Zap className="w-4 h-4" />
                        Get AI Prompts
                    </Link>
                </div>

                {/* Additional Backlinks */}
                <div className="flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm">
                    <a
                        href="https://www.chytbot.com/features"
                        className="text-muted-foreground hover:text-primary transition-colors"
                    >
                        Features
                    </a>
                    <a
                        href="https://www.chytbot.com/pricing"
                        className="text-muted-foreground hover:text-primary transition-colors"
                    >
                        Pricing
                    </a>
                    <a
                        href="https://www.chytbot.com/signup"
                        className="text-muted-foreground hover:text-primary transition-colors"
                    >
                        Get Started Free
                    </a>
                    <a
                        href="https://www.chytbot.com/contact"
                        className="text-muted-foreground hover:text-primary transition-colors"
                    >
                        Contact Support
                    </a>
                </div>
            </div>
        </div>
    );
};
